/**
 * Animates specification values that differ across a product's variants.
 * The section HTML has already been refreshed when on:variant:change fires.
 */

(function () {
  'use strict';

  if (window.DWCProductDetailsLoaded) return;
  window.DWCProductDetailsLoaded = true;

  const originalSpecLists = new WeakMap();
  const normaliseSpecLabel = (value) => (value || '').trim().toLocaleLowerCase();

  function nestSpecificationFields(scope = document) {
    const lists = Array.from(scope.querySelectorAll('.product-spec'));
    if (scope.matches?.('.product-spec')) lists.unshift(scope);
    lists.forEach(function (list) {
      const currentItems = Array.from(list.children);
      let original = originalSpecLists.get(list);
      if (!original || original.items.length !== currentItems.length
        || original.items.some((item) => !currentItems.includes(item))) {
        original = {
          items: currentItems,
          groups: new Map(currentItems.map((item) => [item, item.dataset.specGroup]))
        };
        originalSpecLists.set(list, original);
      }
      const rows = original.items.filter((item) => item.matches('.product-spec__item[data-spec-label]'));
      const parents = new Map();
      rows.forEach(function (row) {
        if (row.dataset.specHiddenWithoutParent === 'true') {
          row.hidden = false;
          delete row.dataset.specHiddenWithoutParent;
        }
        row.classList.remove('dwc-spec-row--nested');
        row.dataset.specGroup = original.groups.get(row) || '';
      });
      rows.forEach(function (row) {
        const target = normaliseSpecLabel(row.dataset.specParent);
        if (!target || row.hidden) return;
        const matches = rows.filter((candidate) => candidate !== row && !candidate.hidden && candidate.dataset.specHasValue !== 'false'
          && normaliseSpecLabel(candidate.dataset.specLabel) === target);
        const groupMatches = matches.filter((candidate) => candidate.dataset.specGroup === row.dataset.specGroup);
        const candidates = groupMatches.length ? groupMatches : matches;
        if (candidates.length === 1) parents.set(row, candidates[0]);
      });
      const invalid = [];
      parents.forEach(function (parent, row) {
        const seen = new Set([row]);
        let current = parent;
        while (current) {
          if (seen.has(current)) {
            invalid.push(row);
            break;
          }
          seen.add(current);
          current = parents.get(current);
        }
      });
      invalid.forEach((row) => parents.delete(row));
      let visibilityChanged;
      do {
        visibilityChanged = false;
        rows.forEach(function (row) {
          if (row.hidden || !normaliseSpecLabel(row.dataset.specParent)) return;
          const parent = parents.get(row);
          if (parent && !parent.hidden) return;
          parents.delete(row);
          if (row.dataset.specHideWithoutParent === 'true') {
            row.hidden = true;
            row.dataset.specHiddenWithoutParent = 'true';
            visibilityChanged = true;
          }
        });
      } while (visibilityChanged);
      const children = new Map();
      parents.forEach(function (parent, child) {
        if (parent.hidden || child.hidden) return;
        if (!children.has(parent)) children.set(parent, []);
        children.get(parent).push(child);
      });
      const desiredItems = [];
      function addChildren(parent) {
        (children.get(parent) || []).forEach(function (child) {
          child.classList.add('dwc-spec-row--nested');
          child.dataset.specGroup = parent.dataset.specGroup;
          desiredItems.push(child);
          addChildren(child);
        });
      }
      original.items.forEach(function (item) {
        if (parents.has(item)) return;
        desiredItems.push(item);
        addChildren(item);
      });
      desiredItems.forEach(function (item, index) {
        if (list.children[index] !== item) list.insertBefore(item, list.children[index] || null);
      });
      let previousField = null;
      let previousVisibleItem = null;
      Array.from(list.children).forEach(function (item) {
        item.classList.remove('dwc-spec-row--before-disclaimer');
      });
      Array.from(list.children).forEach(function (item) {
        if (item.hidden) return;
        if (item.matches('.dwc-spec-disclaimer-row')) {
          if (item.dataset.specNest === 'false') {
            item.dataset.specGroup = '';
            item.style.setProperty('--dwc-spec-disclaimer-inherited-indent', '0px');
            item.classList.remove('dwc-spec-row--right', 'text-end');
            previousVisibleItem = item;
            return;
          }
          const label = previousField?.querySelector(':scope > .product-spec__label');
          const group = previousField?.dataset.specGroup || '';
          const padding = label?.isConnected ? getComputedStyle(label).paddingInlineStart : '';
          item.dataset.specGroup = group;
          item.style.setProperty('--dwc-spec-disclaimer-inherited-indent', padding || (group ? 'var(--dwc-spec-group-indent, 20px)' : '0px'));
          const rightAligned = previousField?.classList.contains('dwc-spec-row--right')
            || label?.classList.contains('product-spec__label--right') || false;
          item.classList.toggle('dwc-spec-row--right', rightAligned);
          item.classList.toggle('text-end', rightAligned);
          if (previousVisibleItem?.matches('.product-spec__item')) {
            previousVisibleItem.classList.add('dwc-spec-row--before-disclaimer');
          }
        } else if (item.matches('.product-spec__item')) {
          previousField = item;
        }
        previousVisibleItem = item;
      });
      original.items.filter((item) => item.matches('.dwc-spec-group-title:not(.dwc-spec-sku-title)'))
        .forEach(function (heading) {
          let next = heading.nextElementSibling;
          let hasRows = false;
          while (next && !next.matches('.dwc-spec-group-title')) {
            if (next.matches('.product-spec__item') && !next.hidden) hasRows = true;
            next = next.nextElementSibling;
          }
          heading.hidden = !hasRows;
        });
      const visibleItems = Array.from(list.children).filter((item) => !item.hidden);
      Array.from(list.children).forEach((item) => item.classList.remove('dwc-spec-row--group-end'));
      visibleItems.forEach(function (item, index) {
        if (item.matches('.product-spec__item')
          && visibleItems[index + 1]?.matches('.dwc-spec-group-title:not(.dwc-spec-sku-title)')) {
          item.classList.add('dwc-spec-row--group-end');
        }
      });
      const notice = list.previousElementSibling;
      if (notice?.matches('.dwc-spec-difference-notice')) {
        notice.hidden = !Array.from(list.querySelectorAll('.dwc-spec-difference-value')).some(function (value) {
          return value.textContent.trim() && !value.closest('li')?.hidden;
        });
      }
    });
  }

  // Refresh the complete tab group so variant-specific tabs can appear/disappear.
  // Keep the customer's open tab when it exists on the newly selected variant.
  document.addEventListener('on:variant:before-replace-element', function (event) {
    const { toReplace, replaceWith } = event.detail;
    nestSpecificationFields(replaceWith);
    if (!toReplace.matches('.dwc-product-tabs')) return;
    const active = toReplace.querySelector('[role="tab"][aria-selected="true"]');
    if (active) {
      const next = replaceWith.querySelector('[id="' + active.id + '"]');
      if (!next) return;
      replaceWith.querySelectorAll('[role="tab"]').forEach(function (tab) {
        const selected = tab === next;
        tab.setAttribute('aria-selected', selected ? 'true' : 'false');
        if (selected) tab.removeAttribute('tabindex');
        else tab.setAttribute('tabindex', '-1');
      });
      replaceWith.querySelectorAll('[role="tabpanel"]').forEach(function (panel) {
        panel.hidden = panel.id !== next.getAttribute('aria-controls');
      });
    } else {
      toReplace.querySelectorAll('details-disclosure').forEach(function (disclosure) {
        const oldContent = disclosure.querySelector('[data-dynamic-variant-content]');
        if (!oldContent) return;
        const nextContent = replaceWith.querySelector('[data-dynamic-variant-content="' + oldContent.dataset.dynamicVariantContent + '"]');
        const nextDetails = nextContent?.closest('details');
        if (nextDetails) nextDetails.open = disclosure.querySelector('details').open;
      });
    }
  });

  function hideUnselectedAccessories() {
    const picker = Array.from(document.querySelectorAll('variant-picker')).find(function (element) {
      return !element.closest('.js-quickbuy, .cc-featured-product');
    });
    if (!picker || picker.variant) return;
    document.querySelectorAll('.dwc-product-tabs').forEach(function (group) {
      const accessoryTab = group.querySelector('[data-dwc-accessories-tab]');
      if (!accessoryTab) return;
      accessoryTab.hidden = true;
      const panel = group.querySelector('[data-dwc-accessories-panel]');
      if (panel) panel.hidden = true;
      const tabs = group.querySelector('tabbed-content');
      if (tabs?.activeTab === accessoryTab) {
        const firstVisible = Array.from(tabs.tabs).find(function (tab) { return !tab.hidden; });
        if (firstVisible) tabs.activateTab(firstVisible);
      }
      if (!Array.from(group.querySelectorAll('[role="tab"], details-disclosure')).some(function (tab) { return !tab.hidden; })) {
        group.hidden = true;
      }
    });
  }

  function initialiseProductDetails() {
    nestSpecificationFields();
    hideUnselectedAccessories();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialiseProductDetails, { once: true });
  } else {
    initialiseProductDetails();
  }

  document.addEventListener('shopify:section:load', function (event) {
    nestSpecificationFields(event.target);
  });

  document.addEventListener('on:variant:change', function () {
    requestAnimationFrame(function () {
      nestSpecificationFields();
      hideUnselectedAccessories();
      document
        .querySelectorAll('.dwc-product-tabs .dwc-spec-value--animate')
        .forEach(function (value) {
          value.classList.remove('is-dwc-variant-changing');
          void value.offsetWidth;
          value.classList.add('is-dwc-variant-changing');
          value.addEventListener(
            'animationend',
            function () {
              value.classList.remove('is-dwc-variant-changing');
            },
            { once: true }
          );
        });
    });
  });
})();

